import {Injectable} from "@angular/core";
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import * as Sentry from '@sentry/angular';

/**
 * Propagation context from Sentry containing trace information.
 */
export interface PropagationContext {
  traceId?: string;
  spanId?: string;
  sampled?: boolean;
}

/**
 * HTTP interceptor that adds the Sentry `sentry-trace` header to outgoing API requests.
 *
 * This enables correlation between Sentry (frontend) and Grafana Tempo (backend):
 * the reverse proxy records the trace id from this header as the `sentry.trace_id`
 * span attribute on its server span. The backend deliberately does NOT join the
 * browser's trace — the reverse proxy makes the sampling decision and roots every
 * backend trace itself.
 *
 * To correlate:
 * 1. Find an error/transaction in Sentry and note its trace ID
 * 2. In Grafana Tempo search `{ span.sentry.trace_id = "<id>" }`
 * 3. Open the matching backend trace(s) for the full server-side picture
 */
@Injectable()
export class TracePropagationInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only add the header when Sentry is running and for API requests
    if (!this.isSentryEnabled() || !this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    // Wrap all trace logic in try-catch - tracing should never block requests
    try {
      const propagationContext = this.getPropagationContext();
      if (!propagationContext?.traceId) {
        return next.handle(req);
      }

      // Generate a span ID for this request (16 hex chars)
      const spanId = this.generateSpanId();

      // Sentry trace header format: traceId-spanId-sampled (1 = sampled, 0 = not)
      const sampled = propagationContext.sampled === false ? '0' : '1';
      const sentryTrace = `${propagationContext.traceId}-${spanId}-${sampled}`;

      const tracedReq = req.clone({
        setHeaders: {
          'sentry-trace': sentryTrace
        }
      });

      return next.handle(tracedReq);
    } catch {
      // Silently proceed without trace headers if anything fails
      return next.handle(req);
    }
  }

  /** @internal Exposed for testing */
  protected isSentryEnabled(): boolean {
    return Sentry.isInitialized();
  }

  /** @internal Exposed for testing */
  protected getPropagationContext(): PropagationContext | null {
    return Sentry.getCurrentScope().getPropagationContext();
  }

  /** @internal Exposed for testing */
  protected isApiRequest(url: string): boolean {
    // Only add to HEC API requests, not Keycloak auth endpoints
    return url.includes('/hec/api/') || url.includes('/api/');
  }

  /** @internal Exposed for testing */
  protected generateSpanId(): string {
    // Generate 16 random hex characters for span ID
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }
}
